import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch } from 'react-redux';
import { useFragment } from 'react-relay/hooks';

import Button, { ButtonProps } from '@material-ui/core/Button';
import { graphql } from 'babel-plugin-relay/macro';

import { useCurrentUser } from '@lib/hooks';

import useReduxMutation, { UseReduxMutationConfig } from 'hooks/useReduxMutation';
import { actionChatActivityOpen } from 'sagas/sagaWatchersChatActivity';

import { ButtonChatActivityOpen_ChatActivity$key } from './__generated__/ButtonChatActivityOpen_ChatActivity.graphql';

const messages = defineMessages({
  label: {
    id: 'deprecated.fragments.ButtonChatActivityOpen.label',
    defaultMessage: 'Reopen chat',
  },
});

export interface ButtonChatActivityOpenProps extends ButtonProps {
  fragmentRef: ButtonChatActivityOpen_ChatActivity$key;
  mutationConfig?: UseReduxMutationConfig;
}

const ButtonChatActivityOpen: React.FC<ButtonChatActivityOpenProps> = ({ fragmentRef, mutationConfig, ...rest }) => {
  const chatActivity = useFragment<ButtonChatActivityOpen_ChatActivity$key>(
    graphql`
      fragment ButtonChatActivityOpen_ChatActivity on ChatActivity {
        _id
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const currentUser = useCurrentUser()!;

  // WORKAROUND use Redux typesafe-actions until we introduce GQL mutations
  const [execute] = useReduxMutation(actionChatActivityOpen);
  const dispatch = useDispatch();
  const handleClick = useCallback(() => {
    execute({
      method: 'put',
      url: `/chatActivities/${chatActivity._id}/assign/${currentUser.id}`,
      ...mutationConfig,
      callbackSuccess: (response) => {
        // WORKAROUND dispatch action with the previous closed/deleted chatActivity to let
        // sagaWatchersChatActivity refresh connections properly
        dispatch(actionChatActivityOpen.success({ data: { ...chatActivity, status: 'assigned' } }));

        if (mutationConfig && mutationConfig.callbackSuccess) {
          mutationConfig.callbackSuccess(response);
        }
      },
    });
  }, [execute, chatActivity, currentUser.id, dispatch, mutationConfig]);

  return (
    <Button onClick={handleClick} {...rest}>
      {formatMessage(messages.label)}
    </Button>
  );
};

export default ButtonChatActivityOpen;
